import {
  getRealPointValue,
  interpolateYAtX,
  XYPoint,
} from '../../../../../components/Highcharts/utils/chartRuler';

export interface SeriesRangeStats {
  seriesName: string;
  chartTitle: string;
  valueDiff: number;
  promedio: number;
  min: number;
  max: number;
}

export interface ChartRangeStatsResult {
  bySeries: SeriesRangeStats[];
  global: {
    valueDiff: number;
    promedio: number;
    min: number;
    max: number;
  } | null;
}

type ChartExportDataGetter = () => { chart: any; title?: string } | null;

export const calculateChartRangeStats = (
  getRegisteredCharts: () => Map<string, ChartExportDataGetter>,
  point1: number,
  point2: number,
): ChartRangeStatsResult => {
  const [t1, t2] = point1 <= point2 ? [point1, point2] : [point2, point1];
  const charts = getRegisteredCharts();
  const bySeries: SeriesRangeStats[] = [];

  let globalSum = 0;
  let globalCount = 0;
  let globalMin = Infinity;
  let globalMax = -Infinity;
  let valueDiffSum = 0;
  let valueDiffCount = 0;

  charts.forEach((getExportData) => {
    const data = getExportData();
    if (!data?.chart) return;
    const { chart } = data;
    const chartTitle = (data as { title?: string }).title || '';
    (chart.series || []).forEach((series: any) => {
      if (!series.visible) return;
      if (series.type !== 'line' && series.type !== 'column' && series.type !== 'area' && series.type !== 'scatter') return;
      if (series.name === 'Comentarios') return;
      const rawPointsList = series.points || series.data || [];
      const points = rawPointsList.map((p: any) => {
        if (Array.isArray(p)) {
          const y = getRealPointValue({ y: p[1] }) ?? p[1];
          return { x: p[0], y, category: undefined };
        }
        const y = getRealPointValue(p) ?? p.y;
        return { x: p.x ?? p.category, y, category: p.category };
      });
      const validPoints = points.filter((p: any) => {
        const x = p.x ?? p.category;
        let xVal: number;
        if (typeof x === 'number') xVal = x;
        else if (typeof x === 'string') xVal = new Date(x).getTime();
        else xVal = Number(x);
        return typeof p.y === 'number' && !Number.isNaN(p.y) && xVal >= t1 && xVal <= t2;
      });

      if (validPoints.length === 0) return;

      const yValues = validPoints.map((p: any) => p.y as number);
      const min = Math.min(...yValues);
      const max = Math.max(...yValues);
      const promedio = yValues.reduce((a: number, b: number) => a + b, 0) / yValues.length;

      const rawPoints: XYPoint[] = points.map((p: any) => {
        let xVal = Number(p.x);
        if (typeof p.x === 'number') xVal = p.x;
        else if (typeof p.category === 'number') xVal = p.category;
        else if (typeof p.x === 'string') xVal = new Date(p.x).getTime();
        return { x: xVal, y: Number(p.y) };
      });
      const y1 = interpolateYAtX(rawPoints, t1);
      const y2 = interpolateYAtX(rawPoints, t2);
      const valueDiff = (y1 != null && y2 != null) ? y2 - y1 : 0;
      bySeries.push({
        seriesName: series.name || 'Serie',
        chartTitle,
        valueDiff,
        promedio,
        min,
        max,
      });

      globalSum += promedio * validPoints.length;
      globalCount += validPoints.length;
      globalMin = Math.min(globalMin, min);
      globalMax = Math.max(globalMax, max);
      if (y1 != null && y2 != null) {
        valueDiffSum += valueDiff;
        valueDiffCount += 1;
      }
    });
  });

  const global = globalCount > 0 ? {
    valueDiff: valueDiffCount > 0 ? valueDiffSum / valueDiffCount : 0,
    promedio: globalSum / globalCount,
    min: globalMin,
    max: globalMax,
  } : null;

  return { bySeries, global };
};
